import { useState } from "react";
import { chromeIntentUrl, currentInApp } from "../../lib/inapp.js";

const DISMISSED = "empire.inapp.dismissed";

function wasDismissed(): boolean {
  try {
    return globalThis.sessionStorage?.getItem(DISMISSED) === "1";
  } catch {
    return false;
  }
}

/**
 * Stops a player BEFORE they build a village this browser is going to forget.
 *
 * Shown over everything, tutorial included, when the page is inside another
 * app's browser (see lib/inapp.ts). Not a wall: "Play here anyway" works, and
 * says plainly what it costs.
 */
export function InAppGate(): JSX.Element | null {
  const [inApp] = useState(currentInApp);
  const [open, setOpen] = useState(() => inApp !== null && !wasDismissed());
  const [copied, setCopied] = useState(false);
  if (!inApp || !open) return null;

  const where = inApp.app ? `${inApp.app}'s built-in browser` : "an app's built-in browser";
  const browser = inApp.android ? "Chrome" : "Safari";

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(location.href);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const stay = (): void => {
    try {
      globalThis.sessionStorage?.setItem(DISMISSED, "1");
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col justify-center bg-bg px-6"
      data-testid="inapp-gate"
    >
      <h2 className="font-display text-2xl text-gold">Open Empire in {browser}</h2>
      <p className="mt-3 text-sm text-ink">
        You are in {where}. It may log you out when you close it. Your village is safe on your
        account — you would just log in again — but {browser} remembers you and is smoother.
      </p>
      <p className="mt-2 text-sm text-ink-dim">
        {inApp.android
          ? "Tap the button below, or use the ⋮ menu and choose “Open in Chrome”."
          : "Tap ··· or the share button and choose “Open in Safari”. Or copy the link and paste it into Safari."}
      </p>

      <div className="mt-6 flex flex-col gap-2">
        {inApp.android ? (
          <a
            href={chromeIntentUrl(location.href)}
            data-testid="inapp-open-chrome"
            className="grid h-12 place-items-center rounded-lg bg-gold text-sm font-bold text-bg"
          >
            Open in Chrome
          </a>
        ) : null}
        <button
          type="button"
          data-testid="inapp-copy"
          onClick={() => void copy()}
          className={`h-12 rounded-lg text-sm font-bold ${
            inApp.android ? "border border-panel-edge bg-panel text-ink" : "bg-gold text-bg"
          }`}
        >
          {copied ? "Link copied — paste it into " + browser : "Copy the link"}
        </button>
        <button
          type="button"
          data-testid="inapp-stay"
          onClick={stay}
          className="h-12 rounded-lg border border-panel-edge bg-panel text-sm font-semibold text-ink"
        >
          Continue here
        </button>
      </div>
    </div>
  );
}
